import { ParticipantDetail } from "@asd/customer-portal-api";
import { SvgIconComponent } from "@mui/icons-material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BusinessIcon from "@mui/icons-material/Business";
import HelpIcon from "@mui/icons-material/Help";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import { LKCLogo, PASLogo } from "../assets";
import { axiosInstance } from "../services/AxiosInstance";
import { default as ConfigurationService } from "../services/ConfigurationService";
import { ORG_META, type OrganizationKey, type OrgMeta } from "./orgConfig";
import { ReportTab } from "./reports";

interface OrganizationConfiguration {
  branding: {
    contactEmail: string;
    help: {
      icon: SvgIconComponent;
      text: string;
      url: string;
    };
    homepageUrl: string;
    logoSrc: string;
    name: string;
    withTM: boolean;
  };
  getPrograms: (token: string) => Promise<string[] | null>;
  participantIdentifier: {
    key: keyof Pick<ParticipantDetail, "medicaidNumber" | "caseNumber">;
    longLabel: string;
    shortLabel: string;
  };
  ppt: {
    showCareProviders: boolean;
    showExpectedUsageForInactiveAuthorizations: boolean;
  };
  reports: {
    tabs: ReportTab[];
  };
  supportCoordinator: {
    enabled: boolean;
    title: "Care Manager" | "Case Manager";
  };
  timezone: "America/Chicago" | "America/New_York";
}

export type Organization = OrgMeta & { configuration: OrganizationConfiguration };

// Map to store programs, on an as-needed basis. If the programs for an org does not exist, it will call the API to populate it
const PROGRAMS_MAP: Record<string, string[] | null> = {};

const getProgramsForOrg = async (token: string, orgKey: string) => {
  if (!PROGRAMS_MAP[orgKey]) {
    try {
      axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const systemConfig = await ConfigurationService.getSystemConfig();

      if (!systemConfig || systemConfig.status !== 200) {
        throw new Error("Failed to retrieve system config");
      }

      PROGRAMS_MAP[orgKey] = systemConfig.data.programs ?? [];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error fetching programs for organization ${orgKey}:`, error);
      PROGRAMS_MAP[orgKey] = null;
    }
  }
  return PROGRAMS_MAP[orgKey];
};

const CONFIGURATION_MAP: Record<string, OrganizationConfiguration> = {
  LKC: {
    branding: {
      contactEmail: "mysdhub@loriknappchoice.com",
      help: {
        icon: HelpIcon,
        text: "Help",
        url: "https://loriknappchoice.com/help/mysdhub/",
      },
      homepageUrl: "https://loriknappchoice.com/",
      logoSrc: LKCLogo,
      name: "Lori Knapp Choice",
      withTM: true,
    },
    getPrograms: (token: string) => getProgramsForOrg(token, "LKC"),
    participantIdentifier: {
      key: "medicaidNumber",
      longLabel: "Medicaid ID",
      shortLabel: "MA ID",
    },
    ppt: {
      showCareProviders: false,
      showExpectedUsageForInactiveAuthorizations: true,
    },
    reports: {
      tabs: [
        { value: "summary", label: "Summary", Icon: AssessmentIcon, indicatorColor: "success.main" },
        { value: "overUsage", label: "Over-Usage", Icon: TrendingUpIcon, indicatorColor: "error.main" },
        { value: "unused", label: "Unused", Icon: HourglassEmptyIcon, indicatorColor: "warning.main" },
        { value: "depleted", label: "Depleted", Icon: HorizontalRuleIcon, indicatorColor: "custom.main" },
        { value: "overtime", label: "Overtime", Icon: MoreTimeIcon, indicatorColor: "primary.main" },
      ],
    },
    supportCoordinator: {
      enabled: true,
      title: "Care Manager",
    },
    timezone: "America/Chicago",
  },
  PAS: {
    branding: {
      contactEmail: "passdhub@passelfdirection.com",
      help: {
        icon: LocalLibraryIcon,
        text: "Training Materials",
        url: "https://passelfdirection.com/help/mysdhub/",
      },
      homepageUrl: "https://passelfdirection.com/",
      logoSrc: PASLogo,
      name: "PAS",
      withTM: false,
    },
    getPrograms: (token: string) => getProgramsForOrg(token, "PAS"),
    participantIdentifier: {
      key: "caseNumber",
      longLabel: "Member/Case ID",
      shortLabel: "Member/Case ID",
    },
    ppt: {
      showCareProviders: true,
      showExpectedUsageForInactiveAuthorizations: false,
    },
    reports: {
      tabs: [
        { value: "summary", label: "Summary", Icon: AssessmentIcon, indicatorColor: "success.main" },
        { value: "division", label: "Division", Icon: BusinessIcon, indicatorColor: "info.main" },
        { value: "overUsage", label: "Over-Usage", Icon: TrendingUpIcon, indicatorColor: "error.main" },
        { value: "unused", label: "Unused", Icon: HourglassEmptyIcon, indicatorColor: "warning.main" },
        { value: "depleted", label: "Depleted", Icon: HorizontalRuleIcon, indicatorColor: "custom.main" },
      ],
    },
    supportCoordinator: {
      enabled: false,
      title: "Case Manager",
    },
    timezone: "America/New_York",
  },
} as const;

export const ORGANIZATIONS_MAP: Record<string, Organization> = Object.fromEntries(
  Object.entries(ORG_META).map(([key, meta]) => [key, { ...meta, configuration: CONFIGURATION_MAP[key] }]),
) as Record<string, Organization>;

declare global {
  interface Window {
    __ORG__?: string;
  }
}

export const DEV_HOSTNAMES = ["localhost", "keycloak"];
const ORG_OVERRIDE_COOKIE = "org-override";

const readCookie = (name: string): string | undefined => {
  return document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`))
    ?.split("=")[1];
};

const getKcContextOrgKey = (): string | undefined => {
  const kcContext = (window as { kcContext?: { properties?: Record<string, unknown> } }).kcContext;
  const orgKey = kcContext?.properties?.PORTAL_ORGANIZATION;
  return typeof orgKey === "string" ? orgKey : undefined;
};

// Org is resolved once, upstream, and this just reads whatever mechanism
// applies to the current app rather than independently re-resolving it:
//
//  - Frontend: server.ts embeds window.__ORG__ into every response.
//  - Keycloak: Keycloakify's environmentVariables mechanism injects
//    PORTAL_ORGANIZATION into kcContext.properties, sourced from the PORTAL_ORGANIZATION env var
//    (see vite.config.ts + docker-compose.yml). Runtime-overridable, same
//    as FRONTEND_URL/BACKEND_URL already are for this app.
//
// On recognized dev hostnames only, a cookie override (set by
// DevOrgSwitcher) takes priority over both, so the switcher works
// identically on either app during local testing.
export const getOrganization = (): Organization => {
  if (DEV_HOSTNAMES.includes(window.location.hostname)) {
    const override = readCookie(ORG_OVERRIDE_COOKIE);
    if (override && override in ORGANIZATIONS_MAP) {
      return ORGANIZATIONS_MAP[override];
    }
  }

  const embedded = window.__ORG__;
  if (embedded && embedded in ORGANIZATIONS_MAP) {
    return ORGANIZATIONS_MAP[embedded];
  }

  const kcOrg = getKcContextOrgKey();
  if (kcOrg && kcOrg in ORGANIZATIONS_MAP) {
    return ORGANIZATIONS_MAP[kcOrg];
  }

  // Fallback is almost impossible to hit, only possible if someone hits a
  // cached/stale HTML response, or a future bug strips the injected script
  return ORGANIZATIONS_MAP.LKC;
};

export const setOrgOverride = (org: OrganizationKey) => {
  document.cookie = `${ORG_OVERRIDE_COOKIE}=${org}; path=/; max-age=86400`;
  window.location.reload();
};
